import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import {
  Button, Card, DataTable, Dialog, Field, Input, Metric, Pagination, Select, Textarea,
} from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { toast } from '../../lib/toast.js';
import { date, number } from '../../lib/format.js';

/**
 * Who a message goes to.
 *
 * "Everyone" is deliberately not the first option. It is the one that cannot
 * be taken back, and a list whose default is the widest blast radius invites
 * a mis-click into it.
 */
const AUDIENCES = [
  { value: 'customers', label: 'Tenants and owners — the rental app' },
  { value: 'brokers', label: 'Agencies — the broker app' },
  { value: 'user', label: 'One person, by account id' },
  { value: 'all', label: 'Everyone, both apps' },
];

/**
 * Two kinds of message, and the difference is who can switch them off.
 *
 * `promotion` honours the promotions preference, which is off unless
 * somebody turned it on — so most people will not receive it, and the panel
 * says so rather than reporting a send that reached nobody.
 */
const TYPES = [
  {
    value: 'promotion',
    label: 'Promotion — only people who opted in',
    hint: 'Offers, launches, anything marketing. The promotions preference is off by default, so this reaches only accounts that switched it on.',
  },
  {
    value: 'service_update',
    label: 'Service update — reaches everyone',
    hint: 'Downtime, price changes, policy. No preference switches this off, so send it only when that is genuinely warranted.',
  },
];

/** Short names for the history table, where the long labels do not fit. */
const AUDIENCE_NAMES = {
  customers: 'Tenants and owners',
  brokers: 'Agencies',
  user: 'One person',
  all: 'Everyone',
};
const TYPE_NAMES = { promotion: 'Promotion', service_update: 'Service update' };

const SENT_COLUMNS = [
  {
    key: 'sent',
    header: 'Sent',
    render: (row) => (
      <div>
        <div>{date(row.sentAt, { withTime: true })}</div>
        <div className="table__muted">{row.sentBy || '—'}</div>
      </div>
    ),
  },
  {
    key: 'message',
    header: 'Message',
    render: (row) => (
      <div>
        <div className="table__primary">{row.title}</div>
        <div className="table__muted">{row.body}</div>
        {row.url ? <div className="table__muted">{`Opens ${row.url}`}</div> : null}
      </div>
    ),
  },
  {
    key: 'audience',
    header: 'Audience',
    render: (row) =>
      row.audience === 'user' && row.userId ? (
        <Link className="table__link" to={`/users/${row.userId}`}>
          One person
        </Link>
      ) : (
        AUDIENCE_NAMES[row.audience] || '—'
      ),
  },
  {
    key: 'type',
    header: 'Kind',
    render: (row) => TYPE_NAMES[row.type] || '—',
  },
  {
    key: 'recipients',
    header: 'Accounts',
    className: 'table__numeric',
    render: (row) => number(row.recipients),
  },
];

/**
 * Custom notifications.
 *
 * Everything here goes through the same path a system notification does: the
 * recipient's preferences, their quiet hours, their registered devices. A
 * broadcast that went around those would be the one notification a person
 * cannot turn off, and the first thing they would do about it is turn the
 * rest off too.
 */
export default function NotificationsPage() {
  const queryClient = useQueryClient();
  const audience = useQuery({ queryKey: ['admin-broadcast-audience'], queryFn: api.notifications.audience });
  const sent = useListQuery({ key: 'admin-broadcasts', fetcher: api.notifications.broadcasts });

  const [form, setForm] = useState({
    audience: 'customers',
    userId: '',
    type: 'promotion',
    title: '',
    body: '',
    url: '',
  });
  const [confirming, setConfirming] = useState(false);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const send = useMutation({
    mutationFn: () =>
      api.notifications.broadcast({
        audience: form.audience,
        ...(form.audience === 'user' ? { userId: form.userId.trim() } : {}),
        type: form.type,
        title: form.title.trim(),
        body: form.body.trim(),
        ...(form.url.trim() ? { url: form.url.trim() } : {}),
      }),
    onSuccess: (result) => {
      toast.success(`Sent to ${number(result.recipients)} account${result.recipients === 1 ? '' : 's'}.`);
      setConfirming(false);
      setForm((current) => ({ ...current, title: '', body: '', url: '' }));
      queryClient.invalidateQueries({ queryKey: ['admin-broadcasts'] });
    },
    onError: (error) => {
      toast.error(error.message);
      setConfirming(false);
    },
  });

  const counts = audience.data || { customers: 0, brokers: 0, all: 0 };
  const reach =
    form.audience === 'user' ? 1 : form.audience === 'all' ? counts.all : counts[form.audience] || 0;

  const selectedType = TYPES.find((entry) => entry.value === form.type);
  const isPromotion = form.type === 'promotion';

  const ready =
    form.title.trim().length >= 3 &&
    form.body.trim().length >= 3 &&
    (form.audience !== 'user' || form.userId.trim().length > 0);

  return (
    <Page
      title="Notifications"
      subtitle="Send a message to the apps. It arrives in the notification list, and as a push where the account allows one."
    >
      <div className="stack">
        <div className="metric-grid">
          <Metric label="Tenants and owners" value={number(counts.customers)} caption="Active accounts" />
          <Metric label="Agencies" value={number(counts.brokers)} caption="Active accounts" />
          <Metric
            label="This message reaches"
            value={number(reach)}
            caption={isPromotion ? 'Before opt-outs are applied' : 'Everyone in the audience'}
            accent={form.audience === 'all' ? 'warning' : undefined}
          />
        </div>

        <Card
          title="Compose"
          subtitle="Written once, delivered through each recipient's own preferences and quiet hours."
          actions={
            <Button variant="primary" onClick={() => setConfirming(true)} disabled={!ready}>
              Review and send
            </Button>
          }
        >
          <div className="stack">
            <div className="field-row">
              <Field label="Audience" id="broadcast-audience">
                <Select id="broadcast-audience" value={form.audience} onChange={set('audience')}>
                  {AUDIENCES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Kind" hint={selectedType?.hint} id="broadcast-type">
                <Select id="broadcast-type" value={form.type} onChange={set('type')}>
                  {TYPES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            {form.audience === 'user' ? (
              <Field
                label="Account id"
                hint="From the account's page in Users — the id in its address bar."
                id="broadcast-user"
              >
                <Input
                  id="broadcast-user"
                  value={form.userId}
                  onChange={set('userId')}
                  placeholder="6aa65893967e9c28cc0931e3"
                />
              </Field>
            ) : null}

            <Field label="Title" hint="Shown on the lock screen. Short enough not to be cut." id="broadcast-title">
              <Input
                id="broadcast-title"
                value={form.title}
                onChange={set('title')}
                placeholder="New listings in Raipur"
              />
            </Field>

            <Field label="Message" id="broadcast-body">
              <Textarea
                id="broadcast-body"
                value={form.body}
                onChange={set('body')}
                placeholder="Twelve flats went live near Shankar Nagar this week."
              />
            </Field>

            <Field
              label="Opens (optional)"
              hint="An in-app path, like /enquiries. Left empty, a tap opens the notification list."
              id="broadcast-url"
            >
              <Input id="broadcast-url" value={form.url} onChange={set('url')} placeholder="/enquiries" />
            </Field>
          </div>
        </Card>

        <Card
          title="Sent"
          subtitle="Everything sent from here, newest first. For a promotion, Accounts counts the whole audience — those without promotions switched on did not receive it."
          bodyless
        >
          <DataTable
            columns={SENT_COLUMNS}
            rows={sent.rows}
            query={sent.query}
            empty={{
              title: 'Nothing sent yet',
              body: 'Notifications sent from this page will be listed here.',
            }}
          />
          <Pagination meta={sent.meta} page={sent.page} onPageChange={sent.setPage} />
        </Card>
      </div>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={`Send to ${number(reach)} account${reach === 1 ? '' : 's'}?`}
        warning={
          isPromotion
            ? 'Delivered only to accounts with promotions switched on, which is off by default — the real number reached will be lower than the figure above. It cannot be recalled once sent, and it is recorded in the audit log against your account.'
            : 'A service update ignores the promotions preference, so it reaches every account in this audience whether they want messages or not. It cannot be recalled once sent, and it is recorded in the audit log against your account.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => send.mutate()} loading={send.isPending}>
              Send now
            </Button>
          </>
        }
      >
        <div className="stack stack--sm">
          <div>
            <strong>{form.title}</strong>
          </div>
          <div className="muted">{form.body}</div>
          {form.url ? <div className="muted">{`Opens ${form.url}`}</div> : null}
        </div>
      </Dialog>
    </Page>
  );
}
